/**
 * One day of the calendar as a list, the way a phone shows it: what is on,
 * in order, with the free stretches between them called out so a slot can be
 * booked with one tap.
 */

/** The studio's day for working out free time. Not a setting yet — nobody has asked to change it. */
export const OPENS_AT_HOUR = 9;
export const CLOSES_AT_HOUR = 21;
/** A gap shorter than this is a breather, not a slot anyone would book. */
export const SHORTEST_SLOT_MINUTES = 30;
const SLOT_ROUNDING_MS = 30 * 60_000;

export interface Span {
  start: Date;
  end: Date;
}

export const dayStart = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Monday of the week the date falls in — the week the studio plans by. */
export function weekStart(date: Date): Date {
  const day = dayStart(date);
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

export const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Whether a span touches the given day at all. */
export function onDay(span: Span, day: Date): boolean {
  const from = dayStart(day).getTime();
  const to = addDays(day, 1).getTime();
  return span.start.getTime() < to && span.end.getTime() > from;
}

/**
 * The free stretches of a day between opening and closing, around whatever
 * is already booked or being worked on. On today, time already past is not
 * offered — the first slot starts at the next half hour.
 */
export function freeGaps(busy: Span[], day: Date, now: Date): Span[] {
  const opens = new Date(day.getFullYear(), day.getMonth(), day.getDate(), OPENS_AT_HOUR);
  const closes = new Date(day.getFullYear(), day.getMonth(), day.getDate(), CLOSES_AT_HOUR);
  let cursor = opens.getTime();
  if (sameDay(day, now)) {
    const nextSlot = Math.ceil(now.getTime() / SLOT_ROUNDING_MS) * SLOT_ROUNDING_MS;
    cursor = Math.max(cursor, nextSlot);
  }
  if (cursor >= closes.getTime() || dayStart(day) < dayStart(now)) return [];

  const sorted = busy
    .map((s) => ({ start: s.start.getTime(), end: s.end.getTime() }))
    .filter((s) => s.end > cursor && s.start < closes.getTime())
    .sort((a, b) => a.start - b.start);

  const gaps: Span[] = [];
  for (const span of sorted) {
    if (span.start - cursor >= SHORTEST_SLOT_MINUTES * 60_000) {
      gaps.push({ start: new Date(cursor), end: new Date(Math.min(span.start, closes.getTime())) });
    }
    cursor = Math.max(cursor, span.end);
    if (cursor >= closes.getTime()) break;
  }
  if (closes.getTime() - cursor >= SHORTEST_SLOT_MINUTES * 60_000) {
    gaps.push({ start: new Date(cursor), end: closes });
  }
  return gaps;
}

/** Stretching on the grid moves in quarter hours, and never below one. */
export const STRETCH_STEP_MINUTES = 15;

/**
 * Where a block ends after its bottom edge is dragged `dyPx` at `pxPerMinute`:
 * snapped to the quarter hour, never shorter than a quarter hour from the start.
 */
export function stretchedEnd(start: Date, end: Date, dyPx: number, pxPerMinute: number): Date {
  const step = STRETCH_STEP_MINUTES * 60_000;
  const moved = Math.round(dyPx / pxPerMinute / STRETCH_STEP_MINUTES) * step;
  return new Date(Math.max(end.getTime() + moved, start.getTime() + step));
}
